/**
 * "Add to PO" / "Remove from PO" on the All Products table — the dashboard
 * twin of Supernova Mobile's Find Product toggle. Both flip the same
 * per-location `product_locations.force_include_auto_po` flag through
 * PUT /products/:id/location-inventory, so a change on either platform shows
 * on the other as soon as the list refreshes.
 */

export const PO_LOCATION_REQUIRED = 'Select a location from the dropdown before changing PO status.';

export const NO_VENDORS_TITLE = 'No Vendors Assigned';
export const NO_VENDORS_MESSAGE =
  "Purchase orders are raised per vendor, so this product can't be added to one until a vendor is assigned.";

/** The list returns `forceIncludeAutoPo` only when location-scoped; a missing
 *  value (or an unscoped list) counts as "not in the PO", like mobile. */
export function isInPo(product: { forceIncludeAutoPo?: boolean | number | string | null }): boolean {
  const v = product.forceIncludeAutoPo;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') return ['true', '1'].includes(v.trim().toLowerCase());
  return false;
}

/** True / false when the row says, null when it doesn't carry vendors. Mobile
 *  blocks "Add" only on an explicit false, so an unknown answer lets the
 *  toggle through rather than blocking a legitimate order. */
export function hasAssignedVendors(product: { payees?: unknown[] | null }): boolean | null {
  if (!Array.isArray(product.payees)) return null;
  return product.payees.length > 0;
}

export function poToggleLabel(inPo: boolean): string {
  return inPo ? 'Remove from PO' : 'Add to PO';
}

export function poToggleSuccessMessage(name: string, nowInPo: boolean): string {
  return nowInPo ? `Added "${name}" to the purchase order.` : `Removed "${name}" from the purchase order.`;
}

export type PoToggleDecision = { kind: 'toggle'; next: boolean } | { kind: 'need-location' } | { kind: 'no-vendors' };

/** What pressing the button should do for this row, mirroring the mobile
 *  guards: a location is required, and adding (never removing) is blocked
 *  when the product explicitly has no vendor. */
export function decidePoToggle(
  product: { forceIncludeAutoPo?: boolean | number | string | null; payees?: unknown[] | null },
  locationId: string | null | undefined,
): PoToggleDecision {
  if (!locationId) return { kind: 'need-location' };
  const next = !isInPo(product);
  if (next && hasAssignedVendors(product) === false) return { kind: 'no-vendors' };
  return { kind: 'toggle', next };
}
